var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { IconButton } from "@stellar/design-system";
import { ASSET_ICON_URLS, getUsdt0ChainById } from "const";
import { isEvmAddress, isSolanaAddress } from "helpers/address";
import { trimInsignificantAmountZeros } from "helpers/amount";
import { capture } from "lib/analytics";
import { ROZO_EVENTS } from "lib/analytics/events";
import { useTxnExpiry } from "pages/txn/useTxnExpiry";
import React from "react";
import QRCode from "react-qr-code";
import { CheckIcon, CopyIcon } from "./Icons";
import { Ethereum, Solana } from "./chains";
import { useIsMobile } from "hooks/useIsMobile";
var validIso = function (value) {
    return value && Number.isFinite(Date.parse(value)) ? value : null;
};
export function getUsdt0DepositOrder(info) {
    var _a, _b, _c;
    var rozo = info.rozo;
    var chain = getUsdt0ChainById(rozo === null || rozo === void 0 ? void 0 : rozo.source_chain_id);
    var amount = (_a = rozo === null || rozo === void 0 ? void 0 : rozo.source_amount) === null || _a === void 0 ? void 0 : _a.trim();
    var address = (_b = rozo === null || rozo === void 0 ? void 0 : rozo.source_receiving_address) === null || _b === void 0 ? void 0 : _b.trim();
    // The intents order's own deadline. Without a valid one the address is not
    // shown at all: a deadline we cannot state may already have passed.
    var orderDeadline = validIso(rozo === null || rozo === void 0 ? void 0 : rozo.expires_at);
    if (!orderDeadline ||
        !(rozo === null || rozo === void 0 ? void 0 : rozo.payment_id) ||
        !chain ||
        !amount ||
        !(Number(amount) > 0) ||
        !address ||
        // Never present an address that does not belong to the order's chain.
        !(chain.key === "ethereum"
            ? isEvmAddress(address)
            : isSolanaAddress(address))) {
        return null;
    }
    return {
        paymentId: rozo.payment_id,
        chainLabel: chain.label,
        chainKey: chain.key,
        amount: trimInsignificantAmountZeros(amount),
        address: address,
        memo: ((_c = rozo.source_memo) === null || _c === void 0 ? void 0 : _c.trim()) || null,
        expiresAt: orderDeadline,
    };
}
var noticeStyle = function (tone) { return (__assign({ padding: "12px 16px", borderRadius: 4, width: "100%" }, (tone === "error"
    ? {
        backgroundColor: "#f8d7da",
        color: "#721c24",
        border: "1px solid #f5c6cb",
    }
    : {
        backgroundColor: "#e7f3ff",
        color: "#004085",
        border: "1px solid #b8daff",
    }))); };
var AWAITING_PAYMENT_STATUSES = new Set([
    "incomplete",
    "pending_user_transfer_start",
]);
/** Per-order USDT payment details; never show an expired address. */
export var Usdt0DepositPayment = function (_a) {
    var _b;
    var info = _a.info;
    var order = getUsdt0DepositOrder(info);
    var isMobile = useIsMobile();
    var timeRemaining = useTxnExpiry((_b = order === null || order === void 0 ? void 0 : order.expiresAt) !== null && _b !== void 0 ? _b : null).timeRemaining;
    var _c = React.useState(null), copied = _c[0], setCopied = _c[1];
    if (info.status !== "expired" &&
        !AWAITING_PAYMENT_STATUSES.has(info.status)) {
        return null;
    }
    if (!order && info.status !== "expired") {
        return (_jsx("div", { className: "TxnInfo__card", "data-testid": "usdt0-order-pending", children: _jsx("div", { style: noticeStyle("info"), role: "status", children: "Your USDT payment order is being prepared. Payment details will appear here shortly. Do not send funds until they do." }) }));
    }
    var isExpired = !order ||
        info.status === "expired" ||
        timeRemaining === "Expired" ||
        Date.parse(order.expiresAt) <= Date.now();
    if (isExpired || !order) {
        return (_jsx("div", { className: "TxnInfo__card", "data-testid": "usdt0-order-expired", children: _jsx("div", { style: noticeStyle("error"), role: "alert", children: "This payment order has expired. Do not send funds to its address. Start a new deposit from your wallet." }) }));
    }
    var copy = function (value, which) {
        navigator.clipboard.writeText(value);
        setCopied(which);
        setTimeout(function () { return setCopied(null); }, 2000);
        if (which === "address") {
            capture(ROZO_EVENTS.DEPOSIT_ADDRESS_COPIED, {
                transaction_id: info.id,
                transaction_kind: info.kind,
            });
        }
    };
    return (_jsxs("div", { className: "TxnInfo__card TxnInfo__card--qr", "data-testid": "usdt0-order", children: [_jsxs("div", { className: "TxnInfo__card-content", style: { backgroundColor: "#fff", width: "100%" }, children: [_jsx("div", { className: "TxnInfo__detail-row", children: _jsxs("div", { style: {
                                display: "flex",
                                alignItems: isMobile ? "flex-start" : "flex-end",
                                justifyContent: "space-between",
                                gap: 12,
                                width: "100%",
                                flexWrap: isMobile ? "wrap" : "nowrap",
                            }, children: [_jsxs("div", { style: { minWidth: 0, flex: "1 1 auto" }, children: [_jsx("span", { className: "TxnInfo__detail-label", style: { whiteSpace: "nowrap" }, children: "Send Exactly Amount" }), _jsxs("span", { className: "TxnInfo__detail-value TxnInfo__detail-value--amount", style: {
                                                display: "block",
                                                fontSize: "clamp(24px, 4vw, 34px)",
                                                lineHeight: 1.1,
                                                fontWeight: 700,
                                                letterSpacing: "-0.01em",
                                                whiteSpace: "nowrap",
                                                fontVariantNumeric: "tabular-nums",
                                                textAlign: "left",
                                                maxWidth: "100%",
                                                marginTop: 4,
                                            }, children: [order.amount, " USDT"] })] }), _jsx("div", { style: {
                                        flexShrink: 0,
                                        display: "flex",
                                        alignItems: "center",
                                        marginLeft: isMobile ? "auto" : undefined,
                                    }, children: _jsx("img", { src: ASSET_ICON_URLS.USDT, alt: "USDT", style: {
                                            width: isMobile ? 44 : 54,
                                            height: isMobile ? 44 : 54,
                                            borderRadius: 12,
                                            objectFit: "cover",
                                        } }) })] }) }), _jsxs("div", { className: "TxnInfo__detail-row", children: [_jsx("span", { className: "TxnInfo__detail-label", children: "Amount" }), _jsxs("div", { className: "TxnInfo__detail-value", children: [_jsxs("span", { className: "TxnInfo__detail-text", children: [order.amount, " USDT"] }), _jsx(IconButton, { altText: "Copy Amount", onClick: function () { return copy(order.amount, "amount"); }, icon: copied === "amount" ? _jsx(CheckIcon, {}) : _jsx(CopyIcon, {}) })] })] }), order.memo && (_jsxs("div", { className: "TxnInfo__detail-row", "data-testid": "usdt0-memo", children: [_jsx("span", { className: "TxnInfo__detail-label", children: "Memo (required)" }), _jsxs("div", { className: "TxnInfo__detail-value", children: [_jsx("span", { className: "TxnInfo__detail-text", children: order.memo }), _jsx(IconButton, { altText: "Copy Memo", onClick: function () { var _a; return copy((_a = order.memo) !== null && _a !== void 0 ? _a : "", "memo"); }, icon: copied === "memo" ? _jsx(CheckIcon, {}) : _jsx(CopyIcon, {}) })] })] })), _jsxs("div", { className: "TxnInfo__detail-row", children: [_jsx("span", { className: "TxnInfo__detail-label", children: "Network" }), _jsxs("span", { className: "TxnInfo__detail-value", "data-testid": "usdt0-network", children: [order.chainKey === "ethereum" ? (_jsx(Ethereum, { width: 20, height: 20 })) : (_jsx(Solana, { width: 20, height: 20 })), order.chainLabel] })] })] }), _jsxs("div", { className: "TxnInfo__qr-wrapper", style: { gap: 12 }, children: [_jsxs("div", { className: "TxnInfo__qr-to-address", children: [_jsx("span", { className: "TxnInfo__qr-to-address-label", children: "To address" }), _jsxs("div", { className: "TxnInfo__qr-to-address-value", children: [_jsx("span", { className: "TxnInfo__qr-to-address-text", children: order.address }), _jsx(IconButton, { altText: "Copy Address", onClick: function () { return copy(order.address, "address"); }, icon: copied === "address" ? _jsx(CheckIcon, {}) : _jsx(CopyIcon, {}) })] })] }), _jsxs("div", { style: { position: "relative", display: "inline-block" }, children: [_jsx(QRCode, { value: order.address, size: 150, bgColor: "#ffffff", fgColor: "#000000", level: "H" }), _jsx("img", { src: ASSET_ICON_URLS.USDT, alt: "USDT", style: {
                                    position: "absolute",
                                    top: "50%",
                                    left: "50%",
                                    transform: "translate(-50%, -50%)",
                                    width: 40,
                                    height: 40,
                                    borderRadius: 8,
                                    backgroundColor: "#fff",
                                } })] })] }), _jsxs("div", { role: "timer", className: "TxnInfo__qr-description", "data-testid": "usdt0-countdown", style: { fontSize: "0.875rem", fontWeight: 600 }, children: ["Time remaining: ", timeRemaining] }), _jsxs("div", { className: "TxnInfo__qr-description", children: [order.memo ? (_jsxs(_Fragment, { children: ["The QR code holds the address only: add the required memo", " ", _jsx("strong", { children: order.memo }), " in your wallet. Without the memo your payment cannot be matched.", " "] })) : null, "Send USDT on ", order.chainLabel, " only. This address is for this order only: do not reuse it, and do not send a different amount, token or network."] })] }));
};
